import React from 'react';

import { config, yup } from 'data';
import { file } from 'helpers';
import { authService, fileService, profileService } from 'services';
import { useForm, useLang, useMutation } from 'hooks';
import { CountrySelect } from 'components/selects';
import { Button, Flex, Form, Input, Paragraph, Title } from 'components/ui';
import { Nullish } from 'types/common';
import { UploadFileLike } from 'types/components';
import { FileType, ProfileStatus } from 'types/models';
import { ProfileUpdateParams } from 'types/services';

import styles from './styles.module.css';

type FormValues = ProfileUpdateParams & {
  idFiles?: Nullish<UploadFileLike[]>;
  selfieFiles?: Nullish<UploadFileLike[]>;
};

const initialValues: FormValues = {
  name: '',
  companyName: '',
  countryCode: config.DEFAULT_COUNTRY,
  phone: '',
};

const filesValidationSchema = yup
  .array()
  .required()
  .length(1)
  .transform((value) => value?.length ? value : null)
  .of(
    yup.mixed<UploadFileLike>().required().file().fileExtension(config.FILE_ALLOWED_EXTENSIONS),
  );

const validationSchema = yup.object({
  name: yup.string().required().min(config.NAME_MIN_LENGTH).max(config.NAME_MAX_LENGTH),
  companyName: yup.string().required().min(config.COMPANY_NAME_MIN_LENGTH).max(config.COMPANY_NAME_MAX_LENGTH),
  countryCode: yup.string().required().country(),
  phone: yup.string().required().phone(),
  idFiles: filesValidationSchema,
  selfieFiles: filesValidationSchema,
});

const InfoRequiredView = () => {
  const form = useForm<FormValues>();
  const lang = useLang();

  const populateProfileMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const idFile = values.idFiles?.shift();
      const selfieFile = values.selfieFiles?.shift();

      delete values.idFiles;
      delete values.selfieFiles;

      const uploadFiles: Record<FileType, UploadFileLike | undefined> = {
        [FileType.ID]: idFile,
        [FileType.SELFIE]: selfieFile,
      };

      const uploadFilePromises = Object.entries(uploadFiles).map(([type, uploadFile]) => {
        if (file.isUploadFile(uploadFile)) {
          return fileService.uploadFile(type as FileType, uploadFile);
        }
      });

      await Promise.all([
        profileService.populateProfile(values),
        ...uploadFilePromises,
      ]);
    },
    onSuccess: () => authService.setStatus(ProfileStatus.WAITING_FOR_APPROVAL),
    successNotification: lang.get('profile.infoRequired.success'),
  });

  return (
    <>
      <Title>{lang.get('profile.infoRequired.heading')}</Title>
      <Paragraph>{lang.get('profile.infoRequired.caption')}</Paragraph>
      <Form
        className={styles.form}
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onFinish={populateProfileMutation.mutateAsync}
      >
        <Form.Item name="name" label={lang.get('common.form.fullName.label')}>
          <Input placeholder={lang.get('common.form.fullName.placeholder')} />
        </Form.Item>
        <Form.Item name="companyName" label={lang.get('common.form.company.label')}>
          <Input placeholder={lang.get('common.form.company.placeholder')} />
        </Form.Item>
        <Form.Item name="countryCode" label={lang.get('common.form.country.label')}>
          <CountrySelect />
        </Form.Item>
        <Form.Item name="phone" label={lang.get('common.form.phone.label')}>
          <Input.Phone />
        </Form.Item>
        <Form.UploadItem
          name="idFiles"
          label={lang.get('common.form.idFile.label')}
          extra={lang.get('common.form.upload.helper', {
            extensions: config.FILE_ALLOWED_EXTENSIONS.join(', '),
            size: config.FILE_MAX_SIZE / 1024 / 1024,
          })}
        >
          <Input.Upload extensions={config.FILE_ALLOWED_EXTENSIONS} />
        </Form.UploadItem>
        <Form.UploadItem
          name="selfieFiles"
          label={lang.get('common.form.selfieFile.label')}
          extra={lang.get('common.form.upload.helper', {
            extensions: config.FILE_ALLOWED_EXTENSIONS.join(', '),
            size: config.FILE_MAX_SIZE / 1024 / 1024,
          })}
        >
          <Input.Upload extensions={config.FILE_ALLOWED_EXTENSIONS} />
        </Form.UploadItem>
        <Flex justify="flex-end">
          <Button htmlType="submit" loading={populateProfileMutation.isPending}>
            {lang.get('common.actions.continue')}
          </Button>
        </Flex>
      </Form>
    </>
  );
};

export default InfoRequiredView;
